import {
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
  Tabs,
  Alert,
} from '@cloudscape-design/components';
import { useParams } from 'react-router';
import { useSplitPanel } from 'contexts/split-panel';
import { AccountTeams } from './teams';
import { AccountIntegrations } from './integrations';
import { AccountClusters } from './clusters';
import { AccountUpgradePlans } from './upgrade_plans';
import CopyText from 'components/copy-text';
import { ValueWithLabel } from 'components/value-with-label';
import { useGetAccount, useGetOrganization } from 'api/admin';
import { formatISO } from 'date-fns';
import { useState } from 'react';
import { AccountAddonInstances } from './addons';
import { AccountCloudConnections } from './cloud_connections';
import { AccountUpgradeTemplates } from './upgrade_templates';
import { AccountUpgradeAssessments } from './upgrade_assessments';

export function AccountOverview(): JSX.Element {
  const { orgId, accountId } = useParams();
  const [errors, setError] = useState<string[]>([]);
  const updateSplitPanel = useSplitPanel();
  const { data } = useGetAccount(accountId || '');
  const orgDetails = useGetOrganization(orgId || '');

  return (
    <ContentLayout header={<Header variant="h1">Account {accountId}</Header>}>
      <SpaceBetween direction="vertical" size="m">
        <Container header={<Header variant="h3">Details</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <ValueWithLabel label="Account ID">
              <CopyText
                copyText={accountId || ''}
                copyButtonLabel="Copy Account ID"
                successText="Account ID copied"
                errorText="Account ID failed to copy"
              />
            </ValueWithLabel>
            <ValueWithLabel label="Account Slug">
              <CopyText
                copyText={data?.slug || ''}
                copyButtonLabel="Copy Account Slug"
                successText="Account Slug copied"
                errorText="Account Slug failed to copy"
              />
            </ValueWithLabel>
            <ValueWithLabel label="Created">
              {data?.created ? formatISO(data.created) : 'N/A'}
            </ValueWithLabel>
            <ValueWithLabel label="Organization ID">
              <CopyText
                copyText={orgDetails?.data?.id || ''}
                copyButtonLabel="Copy Organization ID"
                successText="Organization ID copied"
                errorText="Organization ID failed to copy"
              />
            </ValueWithLabel>
            <ValueWithLabel label="Organization Name">
              <CopyText
                copyText={orgDetails?.data?.name || ''}
                copyButtonLabel="Copy Organization Name"
                successText="Organization Name copied"
                errorText="Organization Name failed to copy"
              />
            </ValueWithLabel>
          </ColumnLayout>
        </Container>
        {errors.map((error, idx) => (
          <Alert
            dismissAriaLabel="Close alert"
            dismissible={true}
            type="error"
            key={idx}
            onDismiss={() => setError([])}
          >
            {error}
          </Alert>
        ))}
        <Tabs
          variant="container"
          tabs={[
            {
              label: 'Clusters',
              id: 'clusters',
              content: (
                <AccountClusters
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Addons',
              id: 'addons',
              content: (
                <AccountAddonInstances
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Upgrade Plans',
              id: 'upgradePlans',
              content: (
                <AccountUpgradePlans
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Upgrade Templates',
              id: 'upgradeTemplates',
              content: (
                <AccountUpgradeTemplates
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Upgrade Assessments',
              id: 'upgradeAssessments',
              content: (
                <AccountUpgradeAssessments
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Integrations',
              id: 'integrations',
              content: (
                <AccountIntegrations
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Cloud Connections',
              id: 'cloudConnections',
              content: (
                <AccountCloudConnections
                  orgId={orgId || ''}
                  accountId={accountId || ''}
                />
              ),
            },
            {
              label: 'Teams',
              id: 'teams',
              content: (
                <AccountTeams orgId={orgId || ''} accountId={accountId || ''} />
              ),
            },
          ]}
          onChange={() => updateSplitPanel(undefined)}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
